import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import PageContainer from '../../components/container/PageContainer';
import Breadcrumb from '../../layouts/full-layout/breadcrumb/Breadcrumb';
import {
  receptionnerManyCmd,
  searchCmdPublicByRef,
  updateCmdMultiStatus,
  viewCmdFournisseur,
  acceptCmdFournisseur,
  resolveManyCmd,
  resolveCmd,
  updateManyCmdFournisseur,
} from '../../redux/slices/commande-fournisseur.slice';
import {
  CmdFbStatusBg,
  CmdFournisseurStatus,
  retardExcludedStatuses,
} from '../../utils/commande.enums';
import CmdFbDetailsWidget from './CmdFbDetails.widget.js';
import { useQrCameraScanner } from '../../hooks/useQrCameraScanner.js';
import { ScanFeedbackToast, useHardwareQrScanner } from '../../hooks/useHardwareQrScannerV2.js';

// ---- MUI icons ----
import SearchIcon from '@mui/icons-material/Search';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import BlockIcon from '@mui/icons-material/Block';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import TagIcon from '@mui/icons-material/Tag';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FiberNewOutlinedIcon from '@mui/icons-material/FiberNewOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import TransformOutlinedIcon from '@mui/icons-material/TransformOutlined';
import DoneAllOutlinedIcon from '@mui/icons-material/DoneAllOutlined';
import { MONO } from '../../utils/helper.js';
import CmdFournisseurFacture from './CmdFournisseurFacture.js';
import dayjs from 'dayjs';

const BCrumb = [
  {
    to: '/App',
    title: 'Home',
  },
  {
    to: '/app/achat',
    title: 'commande',
  },
  {
    title: 'Action en lots',
  },
];

/* ==================================================================
 * Bulk actions per status (replaces the free "Status" select).
 * The button set comes from the statuses of the orders in the list.
 * ================================================================== */
const annuler = (label) => ({
  label,
  color: '#c54141',
  icon: <CancelOutlinedIcon />,
  run: (h) => h.setStatus(CmdFournisseurStatus.ANNULER),
});

const NOUVEL_ACTIONS = [
  {
    label: 'Valider',
    color: '#2563EB',
    icon: <CheckCircleOutlineIcon />,
    run: (h) => h.validate(),
  },
  annuler('Annuler'),
];

const ACTION_COLORS = {
  success: '#16A34A',
  danger: '#DC2626',
  primary: '#2563EB',
  warning: '#D97706',
  info: '#0891B2',
  purple: '#7C3AED',
};

const STATUS_ACTIONS = {
  nouvel: NOUVEL_ACTIONS,

  'dessin-en-cours': NOUVEL_ACTIONS,

  'en-attente': [
    {
      label: 'Mettre comme Accepté',
      color: ACTION_COLORS.success,
      icon: <CheckCircleOutlineIcon />,
      run: (h) => h.showDateLiv(),
    },
    {
      label: 'Mettre comme Refusé',
      color: ACTION_COLORS.danger,
      icon: <CancelOutlinedIcon />,
      run: (h) => h.setStatus(CmdFournisseurStatus.REFUSER),
    },
    {
      label: 'Mettre comme Nouvel',
      color: ACTION_COLORS.primary,
      icon: <FiberNewOutlinedIcon />,
      run: (h) => h.setStatus(CmdFournisseurStatus.NOUVEL),
    },
    annuler('Annuler'),
  ],

  'en-cours': [
    {
      label: 'Envoyer les commandes',
      color: ACTION_COLORS.success,
      icon: <SendOutlinedIcon />,
      run: (h) => h.envoie(),
    },
    {
      label: 'Mettre comme En-attente',
      color: ACTION_COLORS.warning,
      icon: <HourglassEmptyOutlinedIcon />,
      run: (h) => h.setStatus(CmdFournisseurStatus.EN_ATTENTE),
    },
    {
      label: 'Mettre comme Nouvel',
      color: ACTION_COLORS.primary,
      icon: <FiberNewOutlinedIcon />,
      run: (h) => h.setStatus(CmdFournisseurStatus.NOUVEL),
    },
    annuler('Mettre comme Annulé'),
  ],

  'a-saisir': [
    {
      label: 'Mettre en-étiquetage',
      color: ACTION_COLORS.warning,
      icon: <LabelOutlinedIcon />,
      run: (h) => h.setStatus(CmdFournisseurStatus.EN_ETIQUETAGE),
    },
    annuler('Mettre comme Annulé'),
  ],

  'en-etiquetage': [
    {
      label: 'Mettre à dispatcher',
      color: ACTION_COLORS.info,
      icon: <AccountTreeOutlinedIcon />,
      run: (h) => h.dispatchItems(),
    },
    annuler('Mettre comme Annulé'),
  ],

  'failed-cq': [
    {
      label: 'Résoudre',
      color: ACTION_COLORS.success,
      icon: <CheckCircleOutlineIcon />,
      run: (h) => h.resolve('mark-resolus'),
    },
    {
      label: 'Refabriquer',
      color: ACTION_COLORS.warning,
      icon: <BuildOutlinedIcon />,
      run: (h) => h.resolve('refab'),
    },
    {
      label: 'Convertir et créer une autre',
      color: ACTION_COLORS.purple,
      icon: <TransformOutlinedIcon />,
      run: (h) => h.resolve('transform'),
    },
    annuler('Mettre comme Annulé'),
  ],

  'envoye-mg': [
    {
      label: 'Mettre comme reçus',
      color: ACTION_COLORS.success,
      icon: <Inventory2OutlinedIcon />,
      run: (h) => h.setStatus(CmdFournisseurStatus.PRETE),
    },
    annuler('Mettre comme Annulé'),
  ],

  prete: [
    {
      label: 'Mettre à expédier',
      color: ACTION_COLORS.info,
      icon: <LocalShippingOutlinedIcon />,
      run: (h) => h.setStatus(CmdFournisseurStatus.A_EXPEDIE),
    },
    annuler('Mettre comme Annulé'),
    {
      label: 'Mettre comme Livré',
      color: ACTION_COLORS.success,
      icon: <DoneAllOutlinedIcon />,
      run: (h) => h.setStatus(CmdFournisseurStatus.LIVRE),
    },
  ],

  'a-expedier': [
    {
      label: 'Mettre comme livré',
      color: ACTION_COLORS.success,
      icon: <DoneAllOutlinedIcon />,
      run: (h) => h.setStatus(CmdFournisseurStatus.LIVRE),
    },
    annuler('Mettre comme Annulé'),
  ],
};

// What does an action really do? (probe the run() with a recorder, no side effect.)
// Used to recognise the same action across several statuses (e.g. every "cancel").
const actionKey = (d) => {
  let key = '';
  const rec = (name) => (arg) => {
    key = arg !== undefined ? `${name}:${arg}` : name;
  };
  d.run({
    validate: rec('validate'),
    setStatus: rec('setStatus'),
    showDateLiv: rec('showDateLiv'),
    envoie: rec('envoie'),
    dispatchItems: rec('dispatchItems'),
    resolve: rec('resolve'),
  });
  return key;
};

const BULK_STATUS_ACTIONS = Object.fromEntries(
  Object.entries(STATUS_ACTIONS).map(([st, defs]) => [
    st,
    defs.map((d) => ({ ...d, key: actionKey(d) })),
  ]),
);

/* ==================================================================
 * Presentation helpers (flat colors, compact, no gradients)
 * ================================================================== */
const C = {
  ok: '#22c55e',
  err: '#ef4444',
  info: '#0ea5e9',
  indigo: '#6366f1',
  violet: '#8b5cf6',
  amber: '#f59e0b',
  slate: '#64748b',
};

const cardBg = (t) =>
  t.palette.mode === 'dark' ? 'rgba(255,255,255,.025)' : t.palette.background.paper;

const statusLabel = (s) => String(s ?? '---').replace(/[-_]/g, ' ');

const MiniChip = ({ color, icon, label, title, solid, textColor }) => (
  <Box
    title={title ?? (typeof label === 'string' ? label : undefined)}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      height: 22,
      px: 0.85,
      borderRadius: 1,
      bgcolor: solid ? color : alpha(color, 0.15),
      color: solid ? (textColor ?? '#fff') : color,
      fontSize: 11.5,
      fontWeight: 700,
      maxWidth: '100%',
      minWidth: 0,
      flexShrink: 0,
      '& svg': { fontSize: 14, flexShrink: 0 },
    }}
  >
    {icon}
    <Box
      component="span"
      sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    >
      {label}
    </Box>
  </Box>
);

const IconTile = ({ icon, color, size = 28 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: 1.25,
      display: 'grid',
      placeItems: 'center',
      flexShrink: 0,
      bgcolor: alpha(color, 0.15),
      color,
      '& svg': { fontSize: size * 0.62 },
    }}
  >
    {icon}
  </Box>
);

/* ==================================================================
 * Main container
 * ================================================================== */
const CmdBulkActionContainer = ({ pickup_mode }) => {
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.cmd_achat_store);
  // ⚠️ adapt to wherever you already get lst_mg in the other page
  const lst_mg = useSelector((state) => state.magasin_store?.lst_mg);

  const [lst_cmd, set_lst_cmd] = useState([]);
  const [lst_cmd_serach, set_lst_cmd_serach] = useState([]);
  const [sel_item, set_sel_item] = useState(null);
  const [show_details, set_show_details] = useState(false);
  const [show_date_liv_modal, set_show_date_liv_modal] = useState(false);
  const [date_liv, set_date_liv] = useState('');
  const [selected_cmd, set_selected_cmd] = useState([]);
  const [show_envoie_frn_form, set_show_envoie_frn_form] = useState(false);
    const [sel_commande, set_sel_commande] = useState(null);


   const onCloseDateLivModal = () => {
    set_show_date_liv_modal(false);
    set_sel_commande(null);
  };

  const onCloseEnvoieCmdFournisseur = () => {
    set_selected_cmd([]);
    set_show_envoie_frn_form(false);
  };

  const onSearchCommande = (event, value, reason) => {
    dispatch(searchCmdPublicByRef(value))
      .unwrap()
      .then((res) => {
        if (Array.isArray(res)) {
          set_lst_cmd_serach(res);

          if (sel_item) {
            set_sel_item(res.find((el) => el?.ref == sel_item?.ref));
          }
        }
      })
      .catch((err) => console.log(err));
  };

  const onAddCmd = (value) => {
    if (value) set_lst_cmd([value, ...lst_cmd]);
  };
  const onDelCmd = (value) => {
    if (value) set_lst_cmd(lst_cmd.filter((el) => el?.idCmdFournisseur != value));
  };

  /* ---- actions ---- */
  const resetList = () => {
    set_lst_cmd([]);
    set_lst_cmd_serach([]);
  };

  const run = async (thunkAction) => {
    try {
      await dispatch(thunkAction).unwrap();
    } catch (err) {
      console.log(err);
    }
  };

  // low level: (ids, status), returns a promise
  const onUpdateMultiStatus = (ids, status) => {
    if (!ids?.length || !status) return Promise.resolve();
    return run(updateCmdMultiStatus({ ids, status }));
  };

  // "Réceptionner" button (pickup_mode)
  const onReceptionner = async () => {
    if (!lst_cmd?.length) return;
    await run(receptionnerManyCmd({ ids: lst_cmd.map((el) => el?.idCmdFournisseur) }));
    resetList();
  };

  const onDispatchMultiItem = async (commandes) => {
    const lst_tmp = commandes ?? lst_cmd;
    if (!Array.isArray(lst_tmp) || !lst_mg) return;

    const mg_central = lst_mg.find((el) => el?.is_central);
    const idsOf = (pred) => lst_tmp.filter(pred).map((el) => el?.idCmdFournisseur);

    const ids_dispatch = idsOf(
      (el) => el?.magasin?.idMagasin !== mg_central?.idMagasin && el?.cq?.is_valide,
    );
    const ids_en_stock = idsOf(
      (el) => el?.magasin?.idMagasin === mg_central?.idMagasin && el?.cq?.is_valide,
    );
    const ids_failed_cq = idsOf((el) => !el?.cq?.is_valide);

    await onUpdateMultiStatus(ids_dispatch, CmdFournisseurStatus.ENVOYE_MG);
    await onUpdateMultiStatus(ids_en_stock, CmdFournisseurStatus.PRETE);
    await onUpdateMultiStatus(ids_failed_cq, CmdFournisseurStatus.FAILED_CQ);
    resetList();
  };

  const onUpdatemultiItems = async (targetStatus) => {
    if (!Array.isArray(lst_cmd) || !targetStatus) return;

    const has_poincon = lst_cmd.some((el) => el?.fournisseur?.isDisposePoincon);
    if (targetStatus === CmdFournisseurStatus.EN_ETIQUETAGE && has_poincon) {
      const ids_non_poincon = lst_cmd
        .filter((el) => !el?.fournisseur?.isDisposePoincon)
        .map((el) => el?.idCmdFournisseur);
      const poincon = lst_cmd.filter((el) => el?.fournisseur?.isDisposePoincon);

      if (ids_non_poincon.length) await onUpdateMultiStatus(ids_non_poincon, targetStatus);
      if (poincon.length) await onDispatchMultiItem(poincon);
    } else {
      await onUpdateMultiStatus(
        lst_cmd.map((el) => el?.idCmdFournisseur),
        targetStatus,
      );
    }
    resetList();
  };

  const onValidateCmd = () => {
    const sans_fournisseur = lst_cmd.some(
      (el) => !el?.fournisseur || el?.fournisseur?.nomSociete === 'Maquette 3D',
    );
    if (sans_fournisseur) alert('Vous devez choisir un fournisseur !');
    else onUpdatemultiItems(CmdFournisseurStatus.EN_ATTENTE);
  };

  const onEnvoieCmdFournisseur = (cmd) => {
    set_selected_cmd(cmd);
    set_show_envoie_frn_form(true);
  };

  const onEnvoieMultiItem = () => {
    if (Array.isArray(lst_cmd)) {
      const tmp = lst_cmd.filter((el) => el?.selected === true);
      const lst_fb = [...new Set(tmp.map((el) => el?.fournisseur?.idFournisseur))];
      if (lst_fb.length > 1)
        alert('Vous ne pouvez pas envoyer des commandes avec des fournisseurs différents !');
      else if (tmp?.length > 1 && tmp.find((el) => el?.catalogue?.is_sur_mesure))
        alert('Les commandes sur mesure ne peuvent pas être envoyées en groupe !');
      else onEnvoieCmdFournisseur(tmp);
    }
  };

   const onShowDateLivModal = () => {
    if (!Array.isArray(lst_cmd)) return;
    const selected_cmd = lst_cmd.filter((el) => el?.selected === true).length;
    const selected_cmd_client = lst_cmd.filter(
      (el) => el?.selected === true && el?.type_commande === 'commande-client',
    ).length;
    if (selected_cmd === 1 || (selected_cmd > 1 && selected_cmd_client === 0)) {
      set_sel_commande(lst_cmd.filter((el) => el?.selected === true));
      set_show_date_liv_modal(true);
    } else {
      alert('Cette action est par commande pour les commandes clients !');
    }
  };

  const onAcceptCmdFournisseur = (ids, date_liv, status) => {
    if (Array.isArray(ids)) {
      dispatch(updateManyCmdFournisseur({ ids, values: { date_livraison: date_liv } }))
        .unwrap()
        .then(() => {
          dispatch(updateCmdMultiStatus({ ids, status }))
            .unwrap()
            .then(() => {
              resetList();
            })
            .catch((err) => console.log(err));
        })
        .catch((err) => console.log(err));
    }
  };

  const onAcceptMultiItem = (date_liv) => {
    if (Array.isArray(lst_cmd)) {
      onAcceptCmdFournisseur(
        lst_cmd.filter((el) => el?.selected === true).map((el) => el?.idCmdFournisseur),
        date_liv,
        CmdFournisseurStatus.EN_COURS,
      );
      set_show_date_liv_modal(false);
    }
  };

  const onResolveMany = async (idCmdFournisseurs, option) => {
    if (Array.isArray(idCmdFournisseurs) && option)
      try {
        for (const idCmdFournisseur of idCmdFournisseurs) {
          await dispatch(resolveCmd({ idCmdFournisseur, option })).unwrap();
        }
        resetList();
      } catch (error) {
        console.log('onResolveMany :: FAILED', error);
      }
  };

  const onResolveCmd = (option) => {
    if (!Array.isArray(lst_cmd)) return;
    const selected_cmd = lst_cmd.filter((el) => el?.selected === true);
    if (
      option === 'transform' &&
      selected_cmd.some((el) => el?.type_commande === 'commande-reassort')
    ) {
      alert(
        "La transformation d'une commande de réassort en commande de réassort n'est pas autorisée !",
      );
    } else if (selected_cmd?.length) {
      onResolveMany(
        selected_cmd.map((el) => el?.idCmdFournisseur),
        option,
      );
    }
  };

  const bulk_handlers = {
    validate: onValidateCmd,
    setStatus: onUpdatemultiItems,
    showDateLiv: onShowDateLivModal,
    envoie: onEnvoieMultiItem,
    dispatchItems: () => onDispatchMultiItem(),
    resolve: onResolveCmd,
  };

  /* ---- details ---- */
  const onShowDetails = (data) => {
    set_sel_item(data);
    set_show_details(true);
  };
  const onCloseDetails = () => {
    set_sel_item(null);
    set_show_details(false);
  };

  /* ---- scanners ---- */
  const { openScanner, QrScannerModal, closeScanner } = useQrCameraScanner({
    onScan: (value) => {
      const data = JSON.parse(value);
      if (data?.type === 'bon-commande' && data?.id) {
        dispatch(viewCmdFournisseur(data?.id))
          .unwrap()
          .then((res) => {
            onAddCmd(res);
            closeScanner();
          })
          .catch((err) => console.log(err));
      }
    },
  });

  const handleScan = (value) => {
    const data = JSON.parse(value);
    if (data?.type === 'bon-commande' && data?.id) {
      dispatch(viewCmdFournisseur(data?.id))
        .unwrap()
        .then((res) => {
          onAddCmd(res);
          closeScanner();
        })
        .catch((err) => console.log(err));
    }
  };

  const { lastScan } = useHardwareQrScanner(
    (data, raw) => {
      if (!data) {
        console.warn('Malformed scan:', raw);
        return;
      }
      handleScan(data);
    },
    { parseJson: true },
  );

  /* ---- display-only ---- */
  const nb = lst_cmd?.length ?? 0;
  const apply_disabled = nb == 0 || loading;

  // statuses present in the list -> available actions, enabled only when valid for ALL the orders
  const status_counts = (lst_cmd ?? []).reduce((acc, el) => {
    const st = el?.status ?? '---';
    acc[st] = (acc[st] || 0) + 1;
    return acc;
  }, {});
  const present_statuses = Object.keys(status_counts);
  const bulk_actions = (() => {
    const map = new Map();
    present_statuses.forEach((st) =>
      (BULK_STATUS_ACTIONS[st] ?? []).forEach((d) => {
        if (!map.has(d.key)) map.set(d.key, d);
      }),
    );
    return [...map.values()];
  })();
  // returns a reason when the button must be disabled, null when enabled
  const bulk_reason = (d) => {
    if (loading) return 'Opération en cours...';
    const lacking = present_statuses.filter(
      (st) => !(BULK_STATUS_ACTIONS[st] ?? []).some((a) => a.key === d.key),
    );
    if (lacking.length)
      return `Non applicable au statut : ${lacking.map((st) => statusLabel(st)).join(', ')}`;
    return null;
  };

  return (
    <PageContainer title="Réception des commandes" description="">
      {/* breadcrumb */}
      <Breadcrumb title={'Action par lots'} items={BCrumb} />
      {/* end breadcrumb */}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* ---------- Search + scanner ---------- */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            flexWrap: 'wrap',
            p: 1.25,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: cardBg,
          }}
        >
          <IconTile
            icon={pickup_mode ? <MoveToInboxIcon /> : <PlaylistAddCheckIcon />}
            color={pickup_mode ? C.ok : C.indigo}
            size={36}
          />
          <Box sx={{ display: { xs: 'none', sm: 'block' }, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', lineHeight: 1.2 }}>
              {pickup_mode ? 'Réception' : 'Modification par lots'}
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
              Recherchez ou scannez les commandes à traiter
            </Typography>
          </Box>

          <Autocomplete
            sx={{ flex: '1 1 320px', minWidth: 240 }}
            options={Array.isArray(lst_cmd_serach) ? lst_cmd_serach : []}
            getOptionLabel={(el) =>
              el
                ? `${el?.ref} - ${
                    el?.client
                      ? `${el?.client?.nom} ${el?.client?.prenom}`
                      : el?.fournisseur?.nomSociete
                  }`
                : ''
            }
            loading={loading}
            noOptionsText="Aucune commande trouvée"
            loadingText="Recherche..."
            onInputChange={onSearchCommande}
            onChange={(event, newValue) => {
              return onAddCmd(newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder="Num Commande / client ..."
                aria-label="fb"
                sx={{ '& .MuiInputBase-root': { fontSize: '0.88rem', borderRadius: 1.5 } }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 19 }} />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <Button
            size="small"
            onClick={openScanner}
            variant="outlined"
            color="primary"
            startIcon={<QrCodeScannerIcon />}
            sx={{ textTransform: 'none', fontWeight: 700, height: 40 }}
          >
            Scanner QR (CAM)
          </Button>
          <QrScannerModal />
          <ScanFeedbackToast scanner={lastScan} />
        </Box>

        {/* ---------- List ---------- */}
        <CmdList
          loading={loading}
          error={error}
          lst_items={lst_cmd}
          onShowCmdDetails={onShowDetails}
          onDelItem={onDelCmd}
        />

        {/* ---------- Sticky action bar ---------- */}
        <Box
          sx={{
            position: 'sticky',
            bottom: 8,
            zIndex: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            px: 1.5,
            py: 1,
            borderRadius: 2,
            border: '1px solid',
            borderColor: nb ? alpha(C.ok, 0.5) : 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <MiniChip
              color={nb ? C.ok : C.slate}
              icon={<FactCheckOutlinedIcon />}
              label={`${nb} commande${nb > 1 ? 's' : ''} sélectionnée${nb > 1 ? 's' : ''}`}
            />
            {/* statuses present in the list */}
            {present_statuses.map((st) => (
              <MiniChip
                key={`st-${st}`}
                color={CmdFbStatusBg?.[st] || '#808080'}
                icon={<FlagOutlinedIcon />}
                label={`${statusLabel(st)} × ${status_counts[st]}`}
                title="Statut des commandes de la liste"
              />
            ))}

            <Box sx={{ flex: 1 }} />

            {pickup_mode && (
              <Button
                onClick={onReceptionner}
                disabled={apply_disabled}
                variant="contained"
                color="success"
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <MoveToInboxIcon />
                }
                sx={{ px: 3, textTransform: 'none', fontWeight: 800, boxShadow: 'none' }}
              >
                Réceptionner
              </Button>
            )}
          </Box>

          {nb === 0 && (
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
              Ajoutez des commandes pour voir les actions disponibles selon leur statut.
            </Typography>
          )}

          {nb > 0 && bulk_actions.length === 0 && (
            <MiniChip
              color={C.amber}
              icon={<InfoOutlinedIcon />}
              label="Aucune action disponible pour les statuts de la liste"
            />
          )}

          {bulk_actions.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {bulk_actions.map((d) => {
                const reason = bulk_reason(d);
                const is_disabled = !!reason;
                return (
                  <Tooltip
                    key={d.key}
                    title={reason ?? ''}
                    arrow
                    disableHoverListener={!is_disabled}
                  >
                    {/* span: lets the tooltip work on a disabled button */}
                    <span>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={d.icon}
                        onClick={() => d.run(bulk_handlers)}
                        disabled={is_disabled}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          borderRadius: 1.5,
                          boxShadow: 'none',
                          backgroundColor: d.color,
                          color: '#fff',
                          '&:hover': {
                            backgroundColor: d.color,
                            boxShadow: 'none',
                            filter: 'brightness(0.9)',
                          },
                          '&.Mui-disabled': {
                            backgroundColor: alpha(d.color, 0.12),
                            color: alpha(d.color, 0.55),
                          },
                        }}
                      >
                        {d.label}
                      </Button>
                    </span>
                  </Tooltip>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

       <CmdDateLivraisonModal
            show={show_date_liv_modal}
            onClose={onCloseDateLivModal}
            onOk={onAcceptMultiItem}
            sel_commande={sel_commande}
          />

      <CmdFournisseurFacture
        commandes={selected_cmd}
        show={show_envoie_frn_form}
        onClose={onCloseEnvoieCmdFournisseur}
        onUpdateMultiStatus={onUpdateMultiStatus}
      />

      <CmdFbDetailsWidget
        idCmdFournisseur={sel_item?.idCmdFournisseur}
        show={show_details}
        onClose={onCloseDetails}
      />

      {/* ---------- Delivery date modal ("Mettre comme Accepté") ---------- */}
      <Dialog
        open={show_date_liv_modal}
        onClose={() => set_show_date_liv_modal(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Date de livraison prévue</DialogTitle>
        <DialogContent>
          <TextField
            type="date"
            fullWidth
            size="small"
            sx={{ mt: 1 }}
            value={date_liv}
            onChange={(e) => set_date_liv(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => set_show_date_liv_modal(false)}>Annuler</Button>
          <Button variant="contained" disabled={!date_liv || loading} onClick={onAcceptMultiItem}>
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

/* ==================================================================
 * List — one compact row per order (no horizontal scroll)
 * ================================================================== */
const COLS =
  '44px minmax(190px,1.4fr) minmax(200px,1.6fr) minmax(140px,1fr) minmax(130px,0.9fr) 150px 100px 100px';

const HEADERS = [
  ['', null],
  ['Num Commande', <TagIcon />],
  ['Article', <Inventory2OutlinedIcon />],
  ['Client', <PersonOutlineIcon />],
  ['Fournisseur', <LocalShippingOutlinedIcon />],
  ['Status', <FlagOutlinedIcon />],
  ['Date Prévu', <EventOutlinedIcon />],
  ['Magasin', <StorefrontOutlinedIcon />],
];

const CmdList = ({ lst_items, loading, error, onShowCmdDetails, onDelItem }) => {
  return (
    <Box
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: cardBg,
        overflow: 'hidden',
      }}
    >
      {/* column headers (wide screens) */}
      <Box
        sx={{
          display: { xs: 'none', lg: 'grid' },
          gridTemplateColumns: COLS,
          columnGap: 1.5,
          alignItems: 'center',
          px: 2,
          height: 36,
          bgcolor: (t) => alpha(t.palette.text.primary, 0.04),
        }}
      >
        {HEADERS.map(([label, icon], i) => (
          <Box
            key={i}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.6,
              color: 'text.secondary',
              '& svg': { fontSize: 15 },
            }}
          >
            {icon}
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.4,
              }}
            >
              {label}
            </Typography>
          </Box>
        ))}
      </Box>

      {lst_items.length > 0 ? (
        lst_items.map((el) => (
          <CmdItem
            key={el?.idCmdFournisseur}
            cmd={el}
            onItemClick={onShowCmdDetails}
            onDelItem={onDelItem}
          />
        ))
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
            py: 6,
            color: 'text.disabled',
          }}
        >
          <QrCode2Icon sx={{ fontSize: 44 }} />
          <Typography sx={{ fontSize: '0.92rem', fontWeight: 700 }}>
            Aucune commande dans la liste
          </Typography>
          <Typography sx={{ fontSize: '0.76rem' }}>
            Scannez un bon de commande ou recherchez une commande ci-dessus
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// label shown only below the "lg" breakpoint (column header is hidden there)
const Cell = ({ label, children }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      sx={{
        display: { xs: 'block', lg: 'none' },
        fontSize: '0.6rem',
        fontWeight: 700,
        color: 'text.disabled',
        textTransform: 'uppercase',
        letterSpacing: 0.4,
      }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

const CmdItem = ({ cmd, onItemClick, onDelItem }) => {
  const [blocage_tags, set_blocage_tags] = useState([]);

  useEffect(() => {
    if (Array.isArray(cmd?.bloquages)) {
      let tmp = cmd?.bloquages?.filter((el) => el?.status != 'resolus')?.map((el) => el?.type);
      tmp = [...new Set(tmp)];
      set_blocage_tags(tmp);
    }
  }, [cmd?.bloquages]);

  const statusColor = CmdFbStatusBg?.[cmd?.status] ? CmdFbStatusBg?.[cmd?.status] : 'gray';
  const is_late = cmd?.is_retard && !retardExcludedStatuses.includes(cmd?.status);
  const has_issue =
    !!cmd?.is_urgent || is_late || (Array.isArray(blocage_tags) && blocage_tags.length > 0);
  const stripColor = has_issue ? C.err : statusColor;
  const date_prevu =
    cmd?.is_urgent && cmd?.date_urgent
      ? String(cmd?.date_urgent).slice(0, 10)
      : cmd?.date_livraison_prevu
        ? String(cmd?.date_livraison_prevu).slice(0, 10)
        : '-';
  const article_name = `${cmd?.catalogue?.nom} ${
    cmd?.catalogue?.groupe_model?.name
      ? `(${cmd?.catalogue?.groupe_model?.name} ${cmd?.catalogue?.groupe_model_var})`
      : ''
  }`;

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, minmax(0,1fr))', lg: COLS },
        columnGap: 1.5,
        rowGap: 1,
        alignItems: 'center',
        px: 2,
        py: 1,
        borderTop: '1px solid',
        borderColor: 'divider',
        transition: 'background-color .15s',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 8,
          bottom: 8,
          width: 3,
          borderRadius: 2,
          bgcolor: stripColor,
        },
        '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.04) },
      }}
    >
      <Box>
        <IconButton
          color="error"
          size="small"
          title="Retirer de la liste"
          aria-label="Retirer de la liste"
          onClick={(e) => onDelItem(cmd?.idCmdFournisseur)}
        >
          <DeleteOutlineIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <Cell label="Num Commande">
        <Box
          role="button"
          tabIndex={0}
          title="Voir les détails"
          onClick={(e) => onItemClick(cmd)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onItemClick(cmd);
            }
          }}
          sx={{
            cursor: 'pointer',
            minWidth: 0,
            '&:hover .cmd-ref': { textDecoration: 'underline' },
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: 2,
              borderRadius: 1,
            },
          }}
        >
          <Typography
            className="cmd-ref"
            noWrap
            sx={{ fontFamily: MONO, fontWeight: 800, fontSize: '0.86rem', color: 'primary.main' }}
          >
            {cmd?.ref}
          </Typography>
          {cmd?.ref_monday && (
            <Typography
              noWrap
              sx={{ fontFamily: MONO, fontSize: '0.72rem', color: 'text.secondary' }}
            >
              {cmd?.ref_monday}
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: has_issue ? 0.5 : 0 }}>
            {cmd?.is_urgent && <MiniChip color={C.err} icon={<WhatshotIcon />} label="Urgent" />}
            {Array.isArray(blocage_tags) &&
              blocage_tags?.map((el, idx) => (
                <MiniChip
                  key={`bl-${idx}-${el}`}
                  color={C.err}
                  icon={<BlockIcon />}
                  label={`! ${el}`}
                />
              ))}
            {is_late && (
              <MiniChip
                color={C.err}
                icon={<EventBusyIcon />}
                label={`Retard (${Math.abs(+cmd?.nbr_j_retard)} J)`}
              />
            )}
          </Box>
        </Box>
      </Cell>

      <Cell label="Article">
        <Typography noWrap title={article_name} sx={{ fontWeight: 700, fontSize: '0.86rem' }}>
          {article_name}
        </Typography>
        <Typography
          noWrap
          sx={{ fontWeight: 700, fontSize: '0.78rem', color: 'success.main', fontFamily: MONO }}
        >
          {cmd?.catalogue?.sku}
        </Typography>
      </Cell>

      <Cell label="Client">
        {cmd?.client ? (
          <MiniChip
            color={cmd?.fournisseur?.bg_color || 'gray'}
            solid
            icon={<PersonOutlineIcon />}
            label={`${cmd?.client?.nom} ${cmd?.client?.prenom}`}
          />
        ) : (
          <Typography sx={{ color: 'text.disabled' }}>—</Typography>
        )}
      </Cell>

      <Cell label="Fournisseur">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, minWidth: 0 }}>
          <LocalShippingOutlinedIcon
            sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }}
          />
          <Typography noWrap sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
            {cmd?.fournisseur?.nomSociete || '-'}
          </Typography>
        </Box>
      </Cell>

      <Cell label="Status">
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            height: 24,
            px: 1.25,
            borderRadius: 1,
            border: '1px solid',
            borderColor: statusColor,
            color: CmdFbStatusBg?.[cmd?.status] ? CmdFbStatusBg?.[cmd?.status] : 'text.secondary',
            bgcolor: alpha(statusColor === 'gray' ? '#808080' : statusColor, 0.08),
            fontSize: '0.74rem',
            fontWeight: 700,
            textTransform: 'capitalize',
            maxWidth: '100%',
          }}
        >
          <Box
            component="span"
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {cmd?.status || '---'}
          </Box>
        </Box>
      </Cell>

      <Cell label="Date Prévu">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: cmd?.is_urgent && cmd?.date_urgent ? C.err : 'text.secondary',
          }}
        >
          <EventOutlinedIcon sx={{ fontSize: 15 }} />
          <Typography
            noWrap
            sx={{ fontFamily: MONO, fontSize: '0.78rem', fontWeight: 600, color: 'inherit' }}
          >
            {date_prevu}
          </Typography>
        </Box>
      </Cell>

      <Cell label="Magasin">
        <MiniChip
          color={cmd?.magasin?.bg_color || 'gray'}
          solid
          textColor={cmd?.magasin?.nom ? '#000' : '#fff'}
          icon={<StorefrontOutlinedIcon />}
          label={cmd?.magasin?.code || 'central'}
        />
      </Cell>
    </Box>
  );
};



const CmdDateLivraisonModal = ({ show, onClose, onOk, sel_commande }) => {
  const [sel_date, set_sel_date] = useState(null);
  const [earliestDate, setEarliestDate] = useState(null);

  const onSubmit = () => {
    if (sel_commande?.length > 0 && sel_date) {
      onOk(sel_date);
    }
  };

  useEffect(() => {
    if (show && sel_commande?.length > 0) {
      const dates = sel_commande
        .map((cmd) => cmd?.date_livraison_prevu)
        .filter(Boolean)
        .map((d) => String(d).substring(0, 10));

      if (dates.length > 0) {
        const minDate = dates.reduce((a, b) => (a < b ? a : b));
        setEarliestDate(minDate);
        set_sel_date(minDate);
      } else {
        setEarliestDate(null);
        set_sel_date(null);
      }
    }
  }, [sel_commande, show]);

  return (
    <Dialog
      sx={{ '& .MuiDialog-paper': { width: '80%', maxHeight: 435 } }}
      maxWidth="xs"
      open={show}
    >
      <DialogTitle>Acceptation des commandes</DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography variant="body2" mb={1}>
              Date prévue de livraison pour les commandes sélectionnées
            </Typography>
            <TextField
              fullWidth
              onChange={(e) => set_sel_date(e?.target?.value)}
              value={sel_date || ''}
              type="date"
              error={!sel_date}
              size="small"
            />
            {sel_date && earliestDate && dayjs(sel_date).isAfter(earliestDate) && (
              <Alert variant="filled" severity="warning" sx={{ p: 0, px: 1, mt: 1 }}>
                <Typography variant="caption">
                  La date de livraison prévue la plus tôt est le <b>{earliestDate}</b>
                </Typography>
              </Alert>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button onClick={onSubmit} variant="contained" disabled={!sel_date}>
          Confirmer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CmdBulkActionContainer;
